import { readApprovedAddressWithdrawal } from './address-withdrawal';
import {
  assertMergeSources,
  assertSplitAmount,
  assertSplitSource,
  assertTransferRecipient,
  isCommandResult,
} from './coin-arguments';
import {
  type ParsedArgument,
  type ParsedCallArg,
  type ParsedCommand,
  type ParsedMergeCommand,
  type ParsedSplitCommand,
  SendAssertionError,
  type TransferValidationStrategy,
} from './transaction-validation';

export const assertMixedTransfer: TransferValidationStrategy = (context) => {
  const { commands, inputs, gasOwner, expected } = context;
  const addressAmount = readApprovedAddressWithdrawal(context);
  if (addressAmount >= expected.amount) {
    throw new SendAssertionError('it combines address funds outside the approved transfer');
  }
  const transfer = commands[commands.length - 1];
  if (transfer?.$kind !== 'TransferObjects' || transfer.TransferObjects.objects.length !== 1) {
    throw new SendAssertionError('it must transfer exactly one approved coin');
  }
  assertTransferRecipient(transfer.TransferObjects.address, inputs, expected.recipient);
  const [transferred] = transfer.TransferObjects.objects;

  const { split, splitIndex, merge, consolidation } = readMixedTransferCommands(commands);
  assertRedemptionMerge(merge, splitIndex, transferred);
  if (consolidation) assertCoinConsolidation(consolidation, split, inputs);
  assertSplitSource(split, inputs, gasOwner, expected);
  assertSplitAmount(split, inputs, expected.amount - addressAmount);
};

function readMixedTransferCommands(commands: ParsedCommand[]): {
  split: ParsedSplitCommand;
  splitIndex: number;
  merge: ParsedMergeCommand;
  consolidation?: ParsedMergeCommand;
} {
  const consolidation = commands[1]?.$kind === 'MergeCoins' ? commands[1] : undefined;
  const splitIndex = consolidation ? 2 : 1;
  const split = commands[splitIndex];
  const merge = commands[splitIndex + 1];
  if (
    commands.length !== splitIndex + 3 ||
    split?.$kind !== 'SplitCoins' ||
    merge?.$kind !== 'MergeCoins'
  ) {
    throw new SendAssertionError('it combines address funds outside the approved transfer');
  }
  return { split, splitIndex, merge, consolidation };
}

function assertRedemptionMerge(
  merge: ParsedMergeCommand,
  splitIndex: number,
  transferred: ParsedArgument,
): void {
  const { destination, sources } = merge.MergeCoins;
  if (
    !isCommandResult(destination, splitIndex) ||
    sources.length !== 1 ||
    !isCommandResult(sources[0], 0) ||
    !isCommandResult(transferred, splitIndex)
  ) {
    throw new SendAssertionError('it combines address funds outside the approved transfer');
  }
}

function assertCoinConsolidation(
  consolidation: ParsedMergeCommand,
  split: ParsedSplitCommand,
  inputs: ParsedCallArg[],
): void {
  const { destination } = consolidation.MergeCoins;
  const { coin } = split.SplitCoins;
  if (destination.$kind !== 'Input' || coin.$kind !== 'Input' || destination.Input !== coin.Input) {
    throw new SendAssertionError('it consolidates coins outside the approved split');
  }
  assertMergeSources([consolidation], inputs);
}
